<template>
  <section
    class="RunActionButtonView"
    :class="{ 'RunActionButtonView--isLocked': !isRunUnlocked }"
  >
    <div class="RunActionButtonView__buttonContainer">
      <template v-if="canJoinRun">
        <RunStatusButtonView
          class="RunActionButtonView__statusButtonList"
          v-bind="{
            run,
            currentPlayerStatus,
          }"
        />
        <Accordion
          :isExpanded="!!currentPlayerStatus"
        >
          <FormButton
            class="RunActionButtonView__noteButton"
            data-testid="noteButton"
            iconName="chatBubble"
            @click="isEditingStatusNote = true"
          >
            <span v-if="currentPlayerStatus?.note">Edit your note</span>
            <span v-else>Add a note</span>
          </FormButton>
        </Accordion>
      </template>
      <template v-else-if="canReviewRun">
        <FormButton
          data-testid="reviewButton"
          iconName="trophyWithStar"
          @click="handleReviewButtonClick"
        >
          <span v-if="!!review">Change your vote</span>
          <span v-else>Vote for the MVP</span>
        </FormButton>
      </template>
      <RunWaitlistButtonView
        v-else
        v-bind="{run}"
      />
    </div>

    <div
      v-if="!isRunUnlocked"
      class="RunActionButtonView__lockMessageContainer"
    >
      <p
        class="RunActionButtonView__lockMessage"
        data-testid="lockMessage"
      >
        <Icon
          class="RunActionButtonView__lockMessageIcon"
          name="lock"
          size="large"
        />Spots open up {{ formattedRsvpDate }}
      </p>
    </div>

    <Teleport to="body">
      <Transition>
        <EditStatusNoteModalView
          v-if="isEditingStatusNote && currentPlayerStatus"
          :modelValue="currentPlayerStatus"
          @close="isEditingStatusNote = false"
        />
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts" setup>
import Accordion from "@/components/Accordion.vue";
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import useNow from "@/composables/useNow";
import useUpdateStatus from "@/composables/useUpdateStatus";
import formatCountdown from "@/lib/utils/date/formatCountdown";
import useRunReviewStore from "@/stores/useRunReviewStore.ts";
import { CrewSummaryWithPlayerIds, RunAward, RunReviewWithVotes, RunSummary, SeasonSummaryWithPlayerIds, StatusSummaryWithPlayer, SubRequestSummary } from "@shared/lib/database/query.types";
import { parseISO } from "date-fns";
import { computed, onBeforeMount, ref } from "vue";
import RunStatusButtonView from "./RunStatusButtonView.vue";
import RunWaitlistButtonView from "./RunWaitlistButtonView.vue";
import EditStatusNoteModalView from "./modals/EditStatusNoteModalView.vue";

const run = defineModel<RunSummary & {
  crews: Array<CrewSummaryWithPlayerIds>,
  statuses: Array<StatusSummaryWithPlayer>,
  sub_requests: Array<SubRequestSummary>,
  season: SeasonSummaryWithPlayerIds | null,
  awards: Array<RunAward>,
  reviews: Array<RunReviewWithVotes>,
}>({
  required: true,
});

const {
  viewReviewForRun,
  getReviewForRun,
  setReviewForRun,
} = useRunReviewStore();

const {
  now,
} = useNow();

const {
  canJoinRun,
  currentPlayerStatus,
  isRunUnlocked,
  canReviewRun,
} = useUpdateStatus(run);

const isEditingStatusNote = ref(false);

onBeforeMount(() => {
  const _review = run.value.reviews.at(0);

  if (_review) {
    setReviewForRun(run.value.id, _review);
  }
});

const review = computed(() => getReviewForRun(run.value.id));

const formattedRsvpDate = computed(() => {
  if (!run.value.rsvp_at) return;

  return formatCountdown(now.value, parseISO(run.value.rsvp_at));
});

function handleReviewButtonClick () {
  viewReviewForRun(run.value.id, review.value ?? undefined);
}
</script>

<style lang="scss" scoped>
.RunActionButtonView {
  position: relative;

  display: grid;
  padding-inline: var(--buttonPaddingInline, 16px);
  padding-block: var(--buttonPaddingBlock, 16px);
  overflow: hidden;
}

.RunActionButtonView:not(.RunActionButtonView--isLocked):has(.RunStatusButtonView) {
  padding-bottom: 0;
}

.RunActionButtonView__buttonContainer {
  display: grid;

  .RunActionButtonView--isLocked & {
    visibility: hidden;
  }
}

.RunActionButtonView__statusButtonList {
  margin-bottom: var(--buttonPaddingBlock, 16px);
}

.RunActionButtonView__noteButton {
  margin-bottom: var(--buttonPaddingBlock, 16px);
}

.RunActionButtonView__lockMessageContainer {
  position: absolute;
  inset: 0;

  display: grid;
  align-content: center;
}

.RunActionButtonView__lockMessage {
  position: relative;
  top: calc(-2px + var(--fontSafariOffset));

  display: grid;
  gap: 12px;
  justify-items: center;

  color: var(--themeColorNeutral500);

  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.RunActionButtonView__lockMessageIcon {
  color: var(--themeColorNeutral400);
}
</style>
